/**
 * @file utils.ts
 *
 * @fileoverview Helps format some fields for the resume components.
 * Includes helper methods that will make combining fields in groups
 * in the resume easier via configuration
 * (config in the root of the frontend).
 */
import { getResumeDimensions } from '~/jobiqo/components/Job/utils/resume_analytics';
import { analyticsDispatch, EVENTS } from '~/jobiqo/lib/analytics';
import { canEditResume } from '~/jobiqo/lib/auth';
import { User } from '~/jobiqo/models/user';
import { Resume } from '~/jobiqo/types/types';
import { getResumeWizardStepsInfo } from '~/jobiqo/components/Resume/Wizard/config';
import { ParsedUrlQuery } from 'querystring';
import {
  buildRedirectUrlWithDestination,
  sanitizeRedirectUrl,
} from '~/jobiqo/lib/redirect';

/**
 * Conver term objects to simple id's.
 *
 * When it comes to terms in mutations we simply want to provide the id's. Because (similar to above)
 * we sometimes use queries responses and perform mutations with that same data we need to do some cleanup
 * on terms. Removing the unnecessary information and simply return "tid".
 *
 * @parm {any} value
 *  The object for which we want to clean term information.
 */
export const converTermsToIds = (value: any) => {
  if (value === null || value === undefined) {
    return value;
  } else if (Array.isArray(value)) {
    return value.map((v) => converTermsToIds(v));
  } else if (typeof value === 'object') {
    if (value.tid) {
      value = value.tid;
      return value;
    } else {
      const newObj = {};
      Object.entries(value).forEach(([key, v]) => {
        newObj[key] = converTermsToIds(v);
      });
      return newObj;
    }
  }
  return value;
};

/**
 * Dispatches resume completed analytics event.
 * The function is extracted so that it can be used in any wizard step.
 *
 * @param {User} user
 *  Current user.
 * @param {Resume} resume
 *  Current resume.
 */
export const dispatchResumeCompleted = (user, resume) => {
  if (resume) {
    analyticsDispatch(
      EVENTS.RESUME_COMPLETED,
      getResumeDimensions(resume, user),
    );
  }
};

/**
 * Constructs title based on resume academic prefix, first name,
 * last name and academic postfix.
 *
 * @param {Resume} resume
 *  The resume.
 * @returns
 *  Resume title.
 */
export const getResumeTitle = (resume: Resume): string =>
  `${resume.academicPrefix ? resume.academicPrefix + ' ' : ''}${
    resume.firstName
  } ${resume.lastName}${
    resume.academicPostfix ? ', ' + resume.academicPostfix : ''
  } `;

/**
 * Helper function that for given fields from specific Resume section (Skills, ...) check
 * if at least one of the fields has some value.
 *
 * @param {{}} groupFields
 *  Object of fields from Resume section from the config.
 * @param {Resume} resume
 *  The resume.
 * @returns boolean
 *  Returns true if at least one field from section has value, otherwise false.
 */
export const checkResumeSectionFieldHasValues = (
  groupFields: {},
  resume: Resume,
): boolean => {
  // Get graphql Resume section field names.
  const groupFieldsValues: string[] = Object.values(groupFields);

  // Helper function to check if there is some value in the Resume for given field name.
  // We need to check it differently based on type of the field.
  const hasValue = function (fieldName) {
    if (
      typeof resume[fieldName] === 'string' ||
      Array.isArray(resume[fieldName])
    ) {
      return resume[fieldName] && resume[fieldName].length > 0;
    } else if (typeof resume[fieldName] === 'object') {
      return resume[fieldName] && Object.values(resume[fieldName]).length > 0;
    } else if (
      typeof resume[fieldName] === 'boolean' ||
      typeof resume[fieldName] === 'number'
    ) {
      return resume[fieldName];
    } else {
      return resume[fieldName] !== undefined ?? false;
    }
  };

  return groupFieldsValues.some(hasValue);
};

/**
 * Checks if user can edit resume.
 * @param {User | undefined } user - The user object from the database.
 * @param {Resume} resume - the resume object
 * @returns {boolean} - true if user can edit resume, false otherwise.
 */
export function userCanEditResume(resume: Resume, user?: User): boolean {
  return !!user && resume && canEditResume(resume, user);
}

/**
 * Checks if resume has attachments.
 * @param {Resume} resume - the resume object
 * @returns {boolean} - true if resume has attachments, false otherwise.
 */
export function resumeHasAttachments(resume: Resume): boolean {
  return !!resume?.attachments && resume.attachments.length > 0;
}

/**
 * Check if resume has badges.
 * @param {Resume} resume - the resume object.
 * @returns {boolean} - true if resume has badges, false otherwise.
 */
export function resumeHasBadges(resume: Resume): boolean {
  return !!resume?.['badges'] && resume['badges'].length > 0;
}

/**
 * Helper function to prepare new step url based on giver parameters.
 *
 * @param {boolean} isLast
 *  The boolean indicates if step is the last one.
 * @param {number} currentStep
 *  The id of the current resume wizard step.
 * @param {any} resumeWizardStep
 *  Local storage value.
 * @param {any} setResumeWizardStep
 *  Local storage value set function.
 * @param {any} removeResumeWizardStep
 *  Local storage value remove function.
 * @param {ParsedUrlQuery} routerQuery
 *  The parser url query.
 * @param {User} user
 *  The current user.
 * @param {Resume} resume
 *  The current resume.
 *
 * @returns string
 *  The path where user will be redirected.
 */
export function prepareResumeWizardNextUrl(
  isLast: boolean,
  currentStep: number,
  resumeWizardStep,
  setResumeWizardStep,
  removeResumeWizardStep,
  routerQuery: ParsedUrlQuery,
  user?: User,
  resume?: Resume,
): string {
  let nextResumeWizardUrl: string;
  const stepsInfo = getResumeWizardStepsInfo();

  if (isLast) {
    if (user && resume) {
      dispatchResumeCompleted(user, resume);
    }
    if (resumeWizardStep) {
      removeResumeWizardStep();
    }
    if (routerQuery.destination) {
      nextResumeWizardUrl = sanitizeRedirectUrl(
        String(routerQuery.destination),
        '/resume',
      );
    } else {
      nextResumeWizardUrl = '/resume';
    }
  } else {
    setResumeWizardStep(stepsInfo[currentStep + 1].page);
    nextResumeWizardUrl = routerQuery.destination
      ? buildRedirectUrlWithDestination(
          stepsInfo[currentStep + 1].page,
          String(routerQuery.destination),
        )
      : stepsInfo[currentStep + 1].page;
  }

  return nextResumeWizardUrl;
}
